import React, {useMemo, useState} from 'react';

import {PipelineStepDataKind} from 'graphql-client/__generated/graphqlGlobalTypes';
import StepConfigEditorRouterV2 from 'pipelines/generalized-step-config-editor/editors/StepConfigEditorRouterV2';
import {StepConfigEditorContext} from 'pipelines/generalized-step-config-editor/StepConfigEditorContext';
import {PipelineStepRawData} from 'pipelines/PipelineTypes';
import {isValueValid} from 'pipelines/StepShapeUtils';
import Modal from 'ui-library/modals/Modal';
import {JSONValue} from 'util/Types';

import {StaticTemplateId, STATIC_TEMPLATE_UI_SHAPES} from './GeneratedStaticTemplateUIShapes';

export interface ConfigureStaticTemplateModalProps {
  inputDataFrames: Array<PipelineStepRawData<PipelineStepDataKind.DATASET>>;
  onRequestClose: () => void;
  onRequestSubmit: (config: Record<string, JSONValue>) => void;
  staticTemplateId: StaticTemplateId;
}

export default function ConfigureStaticTemplateModal({
  inputDataFrames,
  onRequestClose,
  onRequestSubmit,
  staticTemplateId,
}: ConfigureStaticTemplateModalProps): JSX.Element {
  const [draftStaticTemplateConfigState, setDraftStaticTemplateConfigState] = useState<
    Record<string, JSONValue>
  >({});
  const contextValue = useMemo(
    () => ({
      dataFrameId: inputDataFrames[0].id,
      inputDataFrames: Object.fromEntries(
        inputDataFrames.map((dataFrame, index) => [`INPUT_${index}`, [dataFrame]])
      ),
      clientStateId: `configure-${staticTemplateId}`,
    }),
    [inputDataFrames, staticTemplateId]
  );

  const uiShape = STATIC_TEMPLATE_UI_SHAPES[staticTemplateId];

  const configIsComplete = isValueValid(
    draftStaticTemplateConfigState,
    uiShape.configShape,
    contextValue.inputDataFrames
  );

  return (
    <Modal
      buttons={[{submit: true, text: 'Submit', disabled: !configIsComplete}]}
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        onRequestSubmit(draftStaticTemplateConfigState);
      }}
      title={`Configure ${uiShape.name}`}
    >
      <StepConfigEditorContext.Provider value={contextValue}>
        <StepConfigEditorRouterV2
          config={draftStaticTemplateConfigState}
          configShape={uiShape.configShape}
          onChangeConfig={(config: JSONValue, path: Array<string>, newValue: JSONValue) => {
            setDraftStaticTemplateConfigState({...draftStaticTemplateConfigState, [path[0]]: newValue});
          }}
          uiSchema={uiShape.uiSchema}
        />
      </StepConfigEditorContext.Provider>
    </Modal>
  );
}
